import type { LogContext, LoggerStrategy } from './logger-strategy.interface.js';

/**
 * A logging strategy that outputs colored messages to the console.
 * This is the default strategy and provides a developer-friendly output
 * with color-coded namespaces and timing information.
 *
 * @example
 * ```
 * [app:auth] user logged in +150ms
 * ```
 */
export class ColoredConsoleStrategy implements LoggerStrategy {
  public log(context: LogContext): void {
    const { displayName, color, level, timeDiff, args } = context;

    // Fast path: No arguments
    if (args.length === 0) {
      const consoleMethod = console[level] ?? console.log;
      consoleMethod(
        `%c${displayName}%c  %c+${timeDiff}ms`,
        `color: ${color}; font-weight: bold;${level === 'debug' ? ' font-style: italic' : ''}`,
        'color: inherit',
        'color: #888; font-style: italic',
      );
      return;
    }

    const firstArg = args[0];
    let remainingArgs: unknown[];
    let consumed = 0;
    let msg: unknown = firstArg;

    if (typeof firstArg === 'string') {
      remainingArgs = args.slice(1);

      const regex = /%[Ocdfijos]/g;
      while (regex.exec(firstArg) !== null) {
        consumed++;
      }
    } else {
      // Handle non-string first arg (objects, etc.)
      msg = '%o';
      consumed = 1;
      remainingArgs = args;
    }

    const consumedArgs = remainingArgs.slice(0, consumed);
    const extraArgs = remainingArgs.slice(consumed);

    const consoleMethod = console[level] ?? console.log;

    consoleMethod(
      `%c${displayName}%c ${msg} %c+${timeDiff}ms`,
      `color: ${color}; font-weight: bold;${level === 'debug' ? ' font-style: italic' : ''}`,
      'color: inherit',
      ...consumedArgs,
      'color: #888; font-style: italic',
      ...extraArgs,
    );
  }
}
